import { useCallback, useEffect, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import {
  getOpsQuickbooksConfig,
  getOpsQuickbooksLedger,
  getOpsQuickbooksOAuthStart,
  patchOpsQuickbooksConfig,
  postOpsQuickbooksOAuthDisconnect,
  type OpsQuickbooksLedgerRow,
  type OpsQuickbooksOAuthStatus,
} from "@/lib/opsPaperworkClient";

export function OpsPaperworkQuickbooksSection(props: { setMsg: (s: string) => void }) {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [realmId, setRealmId] = useState("");
  const [serviceItemId, setServiceItemId] = useState("1");
  const [customerRef, setCustomerRef] = useState("1");
  const [customerMatchOrCreate, setCustomerMatchOrCreate] = useState(true);
  const [triggerOrderSubmitted, setTriggerOrderSubmitted] = useState(true);
  const [credentialsPresent, setCredentialsPresent] = useState(false);
  const [oauthConnectAvailable, setOauthConnectAvailable] = useState(false);
  const [oauthStatus, setOauthStatus] = useState<OpsQuickbooksOAuthStatus | null>(null);
  const [ledger, setLedger] = useState<OpsQuickbooksLedgerRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [cr, lr] = await Promise.all([getOpsQuickbooksConfig(), getOpsQuickbooksLedger()]);
    setLoading(false);
    if (cr.ok && cr.data) {
      const c = cr.data.config;
      setEnabled(c.enabled);
      setRealmId(c.realm_id ?? "");
      setServiceItemId(c.invoice_defaults.service_item_id);
      setCustomerRef(c.invoice_defaults.customer_ref);
      setCustomerMatchOrCreate(c.customer_match_mode !== "default_ref");
      setTriggerOrderSubmitted(c.triggers.customer_order_submitted);
      setCredentialsPresent(Boolean(cr.data.credentials_present));
      setOauthConnectAvailable(Boolean(cr.data.oauth_connect_available));
      setOauthStatus(cr.data.oauth_status ?? null);
    }
    if (lr.ok) setLedger((((lr.data as { ledger?: OpsQuickbooksLedgerRow[] }).ledger ?? []) as OpsQuickbooksLedgerRow[]));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave() {
    setLoading(true);
    const r = await patchOpsQuickbooksConfig({
      enabled,
      realm_id: realmId.trim() || null,
      conflict_policy: "skip_if_link_exists",
      customer_match_mode: customerMatchOrCreate ? "match_or_create" : "default_ref",
      triggers: { customer_order_submitted: triggerOrderSubmitted },
      invoice_defaults: { service_item_id: serviceItemId.trim() || "1", customer_ref: customerRef.trim() || "1" },
    });
    setLoading(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    props.setMsg(
      r.data?.credentials_present
        ? "QuickBooks retail config saved. Live invoice push is enabled."
        : "QuickBooks config saved. Connect OAuth or set QUICKBOOKS_ACCESS_TOKEN on the server for live sync.",
    );
    await load();
  }

  async function onConnectOAuth() {
    setLoading(true);
    const r = await getOpsQuickbooksOAuthStart();
    setLoading(false);
    if (!r.ok || !r.data?.authorize_url) {
      props.setMsg(r.ok ? "Missing authorize URL." : r.error);
      return;
    }
    window.location.href = r.data.authorize_url;
  }

  async function onDisconnectOAuth() {
    setLoading(true);
    const r = await postOpsQuickbooksOAuthDisconnect();
    setLoading(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    props.setMsg("QuickBooks OAuth disconnected.");
    await load();
  }

  return (
    <div className="space-y-3">
      <InlineHelp label="How QuickBooks retail works">
        Customer order submitted → one QB Invoice per deal. Design: docs/design/ops_quickbooks_retail.md
      </InlineHelp>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={loading} />
        Enable retail invoice push
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={triggerOrderSubmitted}
          onChange={(e) => setTriggerOrderSubmitted(e.target.checked)}
          disabled={loading || !enabled}
        />
        Push on inventory host customer order submitted
      </label>
      <div className="rounded border border-gray-200 p-2 space-y-2">
        <p className="text-xs text-gray-600 font-medium">QuickBooks connection</p>
        {oauthStatus?.connected ? (
          <p className="text-[11px] text-green-700">
            OAuth connected{oauthStatus.realm_id ? ` (realm ${oauthStatus.realm_id})` : ""}
            {oauthStatus.expires_at ? ` — token expires ${new Date(oauthStatus.expires_at).toLocaleString()}` : ""}
          </p>
        ) : (
          <p className="text-[11px] text-gray-500">
            {oauthConnectAvailable
              ? "Connect with Intuit OAuth (recommended) or use server env QUICKBOOKS_ACCESS_TOKEN for dev."
              : "OAuth not configured on server — use QUICKBOOKS_ACCESS_TOKEN + realm for dev pushes."}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {oauthConnectAvailable ? (
            <button
              type="button"
              className="rounded border px-3 py-1 text-sm"
              disabled={loading}
              onClick={() => void onConnectOAuth()}
            >
              Connect QuickBooks
            </button>
          ) : null}
          {oauthStatus?.connected ? (
            <button
              type="button"
              className="rounded border px-3 py-1 text-sm text-red-700"
              disabled={loading}
              onClick={() => void onDisconnectOAuth()}
            >
              Disconnect
            </button>
          ) : null}
        </div>
      </div>
      <label className="block text-xs text-gray-600">
        QuickBooks realm id (manual override)
        <input
          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono"
          value={realmId}
          onChange={(e) => setRealmId(e.target.value)}
          disabled={loading}
          placeholder="From Intuit developer / company settings"
        />
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={customerMatchOrCreate}
          onChange={(e) => setCustomerMatchOrCreate(e.target.checked)}
          disabled={loading || !enabled}
        />
        Match or create QB customer from POS contact fields (email → name → create)
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600">
          Service item id (QB)
          <input
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono"
            value={serviceItemId}
            onChange={(e) => setServiceItemId(e.target.value)}
            disabled={loading}
          />
        </label>
        <label className="block text-xs text-gray-600">
          Fallback customer ref (QB)
          <input
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono"
            value={customerRef}
            onChange={(e) => setCustomerRef(e.target.value)}
            disabled={loading}
          />
        </label>
      </div>
      <p className="text-[11px] text-gray-500">
        Credentials:{" "}
        {credentialsPresent
          ? "live token + realm ready"
          : "missing OAuth connection or QUICKBOOKS_ACCESS_TOKEN — ledger records stubbed payloads"}
      </p>
      <button type="button" className="rounded border px-3 py-1 text-sm" disabled={loading} onClick={() => void onSave()}>
        Save QuickBooks config
      </button>
      {ledger.length ? (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-2 py-1">When</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Trigger</th>
                <th className="px-2 py-1">Type</th>
                <th className="px-2 py-1">QB id</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="px-2 py-1 whitespace-nowrap">{new Date(row.created_at).toLocaleString()}</td>
                  <td className="px-2 py-1">{row.sync_status}</td>
                  <td className="px-2 py-1 font-mono text-[11px]">{row.trigger_key}</td>
                  <td className="px-2 py-1 font-mono text-[11px]">{row.qb_entity_type}</td>
                  <td className="px-2 py-1 font-mono text-[11px]">{row.qb_entity_id ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-gray-500">No QuickBooks sync rows yet.</p>
      )}
    </div>
  );
}
